import {
    ApolloClient,
    ApolloLink,
    CombinedGraphQLErrors,
    CombinedProtocolErrors,
    HttpLink,
    InMemoryCache
} from '@apollo/client';
import {ErrorLink} from '@apollo/client/link/error';

import {SetContextLink} from '@apollo/client/link/context';
import {userManager} from '@/components/authentication/keycloak.js';
import store from '@/store/store.js';
import {clearCurrentUser, setCurrentUserLoading} from '@/store/slices/authSlice.js';

// Polyfill pour Promise.withResolvers si nécessaire (ES2024)
if (!Promise.withResolvers) {
    Promise.withResolvers = function () {
        let resolve, reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return { promise, resolve, reject };
    };
}

// Link HTTP
const httpLink = new HttpLink({
    uri: window.ENV.BACKEND_URL + '/api/graphql'
});

// Link de debug pour logger les requêtes
const debugLink = new ApolloLink((operation, forward) => {
    if (window.ENV.DEBUG === 'true' || window.ENV.DEBUG === true) {
        console.log(`[GraphQL Request]: ${operation.operationName}`);
    }
    return forward(operation);
});

const multipartHttpLink = new SetContextLink((prevContext, operation) => {
    const isSubscription =
        operation.query.definitions.some(
            (definition) =>
                definition.kind === 'OperationDefinition' &&
                definition.operation === 'subscription'
        );

    return {
        headers: {
            ...prevContext.headers,
            Accept: isSubscription
                ? 'multipart/mixed; subscriptionSpec=1.0, application/json'
                : 'application/json',
        },
    };
});

// Link d'authentification avec refresh automatique
const authLink = new SetContextLink(async (prevContext) => {

    const user = await userManager.getUser();

    if (user) {
        return {
            headers: {
                ...prevContext.headers,
                authorization: `Bearer ${user.access_token}`
            }
        };
    }

    return prevContext;

});

const errorLink = new ErrorLink(({ error, operation, forward }) => {

    if (CombinedGraphQLErrors.is(error)) {
        let unauthorized = false;
        error.errors.forEach(({ message, locations, path }) => {
            console.log(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
            );
            if (message === '401: Unauthorized') {
                unauthorized = true;
            }
        });
        if (unauthorized) {
            processNetworkError(401);
        }
    } else if (CombinedProtocolErrors.is(error)) {
        error.errors.forEach(({ message, extensions }) =>
            console.log(
                `[Protocol error]: Message: ${message}, Extensions: ${JSON.stringify(
                    extensions
                )}`
            )
        );
    } else if (ServerError.is(error)) {
        console.error(`[Server error]: ${error}`);
        processNetworkError(error.statusCode);
    } else {
        console.error(`[Unknown error]: ${error}`);
        processNetworkError(500);
    }

});

const processNetworkError = (code) => {
    void (async () => {
        if (code === 401) {
            // En cas de 401 on tente un signin discret :
            // S'il réussit, l'utilisateur est bien authentifié et la 401 est simplement un problème de droits sur une ressource
            // Sinon, on procède à un logout
            const user = await userManager.signinSilent();
            if (!user) {
                store.dispatch(clearCurrentUser());
                store.dispatch(setCurrentUserLoading(true));
                await userManager.signoutRedirect({
                    post_logout_redirect_uri: window.location.origin
                });
            }
        }
    })();
};

const client = new ApolloClient({
    link: ApolloLink.from([debugLink, errorLink, authLink, multipartHttpLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
        watchQuery: {
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all'
        },
        query: {
            fetchPolicy: 'network-only',
            errorPolicy: 'all'
        }
    }
});

export default client;
